<template>
  <nav class="navbar">
    <div class="nav-container">
      <!-- Logo -->
      <div class="nav-logo">
        <router-link to="/" class="logo-link">
          <AppLogo :small="true" :size="40" />
        </router-link>
      </div>

      <!-- Navigation menu -->
      <div
        class="nav-menu-items"
        :class="{ 'mobile-menu-open': mobileMenuOpen }"
      >
        <router-link
          to="/"
          class="nav-item"
          :class="{ active: $route.path === '/' }"
          @click="closeMobileMenu"
        >
          {{ $t("nav.home") }}
        </router-link>
        <router-link
          to="/universities"
          class="nav-item"
          :class="{ active: $route.path === '/universities' }"
          @click="closeMobileMenu"
        >
          {{ $t("nav.universities") }}
        </router-link>
        <router-link
          to="/courses"
          class="nav-item"
          :class="{ active: $route.path === '/courses' }"
          @click="closeMobileMenu"
        >
          {{ $t("nav.courses") }}
        </router-link>
        <router-link
          to="/recommendations"
          class="nav-item"
          :class="{ active: $route.path === '/recommendations' }"
          @click="closeMobileMenu"
        >
          {{ $t("nav.recommendations") }}
        </router-link>
        <router-link
          to="/trending"
          class="nav-item"
          :class="{ active: $route.path === '/trending' }"
          @click="closeMobileMenu"
        >
          {{ $t("nav.trending") }}
        </router-link>
      </div>

      <!-- Mobile menu toggle -->
      <div class="mobile-menu-toggle" @click="toggleMobileMenu">
        <el-icon><Menu /></el-icon>
      </div>

      <!-- Right section: Language + Theme + User menu -->
      <div class="nav-user">
        <!-- Language selector -->
        <div class="language-group">
          <LanguageSelector />
        </div>

        <!-- Auth buttons group -->
        <div class="auth-group">
          <!-- Authenticated user -->
          <div v-show="authStore.isAuthenticated" class="user-menu">
            <el-dropdown @command="handleUserCommand">
              <div class="user-dropdown">
                <el-avatar :size="32" class="user-avatar">
                  {{ getUserInitials }}
                </el-avatar>
                <span class="username">{{
                  authStore.currentUser?.username
                }}</span>
                <el-icon><ArrowDown /></el-icon>
              </div>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="profile" class="dropdown-item">
                    <el-icon class="item-icon"><User /></el-icon>
                    <span class="item-text">{{ $t("nav.profile") }}</span>
                  </el-dropdown-item>
                  <el-dropdown-item command="messages" class="dropdown-item">
                    <el-icon class="item-icon"><ChatDotRound /></el-icon>
                    <span class="item-text">Messages</span>
                    <el-badge v-if="unreadCount > 0" :value="unreadCount" class="unread-badge" />
                  </el-dropdown-item>
                  <el-dropdown-item command="invitation" class="dropdown-item">
                    <el-icon class="item-icon"><Share /></el-icon>
                    <span class="item-text">{{ $t("nav.myInvitationCode") }}</span>
                  </el-dropdown-item>
                  <el-dropdown-item command="bookmarks" class="dropdown-item">
                    <el-icon class="item-icon"><Star /></el-icon>
                    <span class="item-text">{{ $t("bookmarks.myBookmarks") }}</span>
                  </el-dropdown-item>
                  <el-dropdown-item command="follows" class="dropdown-item">
                    <el-icon class="item-icon"><UserFilled /></el-icon>
                    <span class="item-text">{{ $t("follows.followers") }} & {{ $t("follows.following") }}</span>
                  </el-dropdown-item>
                  <el-dropdown-item divided command="add-course" class="dropdown-item">
                    <el-icon class="item-icon"><Plus /></el-icon>
                    <span class="item-text">{{ $t("nav.addCourse") }}</span>
                  </el-dropdown-item>
                  <el-dropdown-item divided command="logout" class="dropdown-item logout-item">
                    <el-icon class="item-icon"><SwitchButton /></el-icon>
                    <span class="item-text">{{ $t("nav.logout") }}</span>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>

          <!-- Guest user -->
          <div v-show="!authStore.isAuthenticated" class="guest-menu">
            <el-button
              type="text"
              class="login-btn"
              @click="$router.push('/login')"
            >
              {{ $t("nav.login") }}
            </el-button>
            <el-button
              type="primary"
              class="register-btn"
              @click="$router.push('/register')"
            >
              {{ $t("nav.register") }}
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- University Switcher Row (centered) -->
    <div v-show="authStore.isAuthenticated" class="university-row">
      <div class="university-container">
        <UniversitySwitcher />
      </div>
    </div>
    
    <!-- Invitation Code Dialog -->
    <el-dialog
      v-model="showInvitationDialog"
      width="540px"
      :title="$t('invitation.myCode')"
      class="invitation-dialog"
      :append-to-body="true"
      :lock-scroll="true"
      :close-on-click-modal="true"
      :destroy-on-close="true"
    >
      <div v-if="loadingInvitation" class="loading-container">
        <el-skeleton :rows="3" animated />
      </div>
      <div v-else-if="myInvitationCode" class="invitation-content">
        <div class="code-display">
          <div class="code-label">{{ $t("invitation.yourCode") }}</div>
          <div class="code-wrapper">
            <div class="code-value">{{ myInvitationCode.code }}</div>
            <el-button
              text
              :icon="Document"
              @click="copyCode"
              class="copy-icon-btn"
              title="Copy to clipboard"
            />
          </div>
        </div>

        <div class="code-actions">
          <el-button
            type="primary"
            size="large"
            @click="copyCode"
            class="action-btn primary-btn"
          >
            <el-icon><Document /></el-icon>
            <span>{{ $t("invitation.copyCode") }}</span>
          </el-button>
          <el-button
            type="success"
            size="large"
            @click="shareCode"
            class="action-btn success-btn"
          >
            <el-icon><Share /></el-icon>
            <span>{{ $t("invitation.share") }}</span>
          </el-button>
        </div>
        
        <div class="code-info">
          <div class="info-box">
            <el-icon class="info-icon"><InfoFilled /></el-icon>
            <div class="info-content">
              <div class="info-text">{{ $t("invitation.codeInfo") }}</div>
              <el-link
                type="primary"
                @click="viewDetails"
                class="details-link"
                :underline="false"
              >
                {{ $t("invitation.viewDetails") }} →
              </el-link>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </nav>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  Menu,
  Plus,
  Star,
  UserFilled,
  User,
  SwitchButton,
  ArrowDown,
  Share,
  Document,
  InfoFilled,
  ChatDotRound,
} from "@element-plus/icons-vue";
import LanguageSelector from "./LanguageSelector.vue";
import UniversitySwitcher from "./UniversitySwitcher.vue";
import AppLogo from "./AppLogo.vue";
import { invitationApi, type InvitationCode } from "@/api/invitations";
import { messagesApi } from "@/api/messages";
import { useI18n } from "vue-i18n";

const router = useRouter();
const authStore = useAuthStore();
const { t } = useI18n();

// Mobile menu state
const mobileMenuOpen = ref(false);

// Invitation dialog state
const showInvitationDialog = ref(false);
const loadingInvitation = ref(false);
const myInvitationCode = ref<InvitationCode | null>(null);

// Messages state
const unreadCount = ref(0);

// Fetch unread message count
const fetchUnreadCount = async () => {
  if (!authStore.isAuthenticated) {
    unreadCount.value = 0;
    return;
  }
  
  try {
    const response = await messagesApi.getUnreadCount();
    unreadCount.value = response.count;
  } catch (error) {
    console.error("Failed to fetch unread count:", error);
  }
};

// Watch authentication state
watch(
  () => authStore.isAuthenticated,
  (authenticated) => {
    if (authenticated) {
      fetchUnreadCount();
    } else {
      unreadCount.value = 0;
    }
  },
  { immediate: true }
);

// Periodically refresh unread count (every 30 seconds)
let unreadCountInterval: number | null = null;
onMounted(() => {
  if (authStore.isAuthenticated) {
    fetchUnreadCount();
  }
  
  unreadCountInterval = window.setInterval(() => {
    if (authStore.isAuthenticated) {
      fetchUnreadCount();
    }
  }, 30000); // 30 seconds
});

// Cleanup interval on unmount
import { onUnmounted } from "vue";
onUnmounted(() => {
  if (unreadCountInterval !== null) {
    clearInterval(unreadCountInterval);
  }
});

// Toggle mobile menu
const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
};

// Close mobile menu
const closeMobileMenu = () => {
  mobileMenuOpen.value = false;
};

// Get user initials for avatar
const getUserInitials = computed(() => {
  const username = authStore.currentUser?.username;
  if (!username) return "U";
  return username.substring(0, 2).toUpperCase();
});

// Fetch invitation code
const fetchInvitationCode = async () => {
  try {
    loadingInvitation.value = true;
    myInvitationCode.value = await invitationApi.getOrCreateMyCode();
  } catch (error) {
    console.error("Failed to fetch invitation code:", error);
    ElMessage.error("Failed to load invitation code");
  } finally {
    loadingInvitation.value = false;
  }
};

// Copy code to clipboard
const copyCode = async () => {
  if (!myInvitationCode.value) return;
  try {
    await navigator.clipboard.writeText(myInvitationCode.value.code);
    ElMessage.success(t("invitation.copied"));
  } catch (error) {
    console.error("Failed to copy:", error);
    ElMessage.error("Failed to copy code");
  }
};

// Share code
const shareCode = async () => {
  if (!myInvitationCode.value) return;
  const message = t("invitation.shareMessage", { code: myInvitationCode.value.code });
  
  if (navigator.share) {
    try {
      await navigator.share({
        title: "Join KursKompass",
        text: message,
      });
    } catch (error) {
      console.log("Share cancelled or failed:", error);
    }
  } else {
    await copyCode();
    ElMessage.info("Invitation code copied! Share it with your friends.");
  }
};

// View details (go to profile invitations tab)
const viewDetails = () => {
  showInvitationDialog.value = false;
  router.push("/profile?tab=invitations");
};

// Handle user dropdown commands
const handleUserCommand = async (command: string) => {
  switch (command) {
    case "profile":
      router.push("/profile");
      break;
    case "messages":
      router.push("/messages");
      // Reset unread count after navigating to messages
      setTimeout(() => fetchUnreadCount(), 1000);
      break;
    case "invitation":
      showInvitationDialog.value = true;
      fetchInvitationCode();
      break;
    case "bookmarks":
      router.push("/bookmarks");
      break;
    case "follows":
      router.push("/follows");
      break;
    case "add-course":
      router.push("/add-course");
      break;
    case "logout":
      try {
        await ElMessageBox.confirm(
          t("nav.logoutConfirmMessage"),
          t("nav.logoutConfirmTitle"),
          {
            confirmButtonText: t("nav.logoutConfirm"),
            cancelButtonText: t("nav.cancel"),
            type: "warning",
            center: true,
            customClass: "logout-confirmation-dialog",
            confirmButtonClass: "logout-confirm-btn",
            cancelButtonClass: "logout-cancel-btn",
          },
        );
        authStore.logout();
        ElMessage.success(t("nav.logoutSuccess"));
        router.push("/");
      } catch {
        // User cancelled
      }
      break;
  }
};
</script>

<style scoped>
.navbar {
  background: linear-gradient(135deg, #ffffff, #f8fafc);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  position: sticky;
  top: 0;
  z-index: 1000;
}

.nav-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  min-height: 70px;
}

/* University Switcher Row */
.university-row {
  border-top: 1px solid rgba(0,0,0,0.06);
  padding: 10px 0;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  /* 确保这一行不会被页面内容遮挡 */
  position: relative;
  z-index: 100;
}

.university-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50px; /* 确保有足够的高度 */
}

.nav-logo {
  flex-shrink: 0;
}

.logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
  transition: all 0.3s ease;
}

.logo-link:hover {
  transform: scale(1.02);
}

.nav-menu-items {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 8px;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 10px 18px;
  text-decoration: none;
  color: #4a5568;
  font-size: 15px;
  font-weight: 600;
  border-radius: 14px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  background: transparent;
  border: 2px solid transparent;
  white-space: nowrap;
  overflow: hidden;
}

.nav-item::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.08),
    rgba(118, 75, 162, 0.08)
  );
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 12px;
}

.nav-item::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 3px;
  background: linear-gradient(90deg, #667eea, #764ba2);
  transform: translateX(-50%);
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 3px 3px 0 0;
}

.nav-item:hover::before {
  opacity: 1;
}

.nav-item:hover::after {
  width: 80%;
}

.nav-item:hover {
  color: #667eea;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.12);
}

.nav-item.active {
  color: #ffffff;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-color: transparent;
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.35),
              inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.nav-item.active::before,
.nav-item.active::after {
  display: none;
}

.nav-item.active:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4),
              inset 0 1px 0 rgba(255, 255, 255, 0.25);
}

.mobile-menu-toggle {
  display: none;
  cursor: pointer;
  padding: 10px;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 20px;
  color: #4a5568;
  background: transparent;
  border: 2px solid transparent;
}

.mobile-menu-toggle:hover {
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.1),
    rgba(118, 75, 162, 0.1)
  );
  color: #667eea;
  border-color: rgba(102, 126, 234, 0.2);
  transform: scale(1.05);
}

.nav-user {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 15px;
}

.language-group {
  display: flex;
  align-items: center;
}

.university-group {
  display: flex;
  align-items: center;
}

.auth-group {
  display: flex;
  align-items: center;
}

.user-dropdown {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 8px 14px;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: #4a5568;
  background: transparent;
  border: 2px solid transparent;
}

.user-dropdown:hover {
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0.1),
    rgba(118, 75, 162, 0.1)
  );
  color: #667eea;
  border-color: rgba(102, 126, 234, 0.2);
  transform: translateY(-1px);
}

.user-avatar {
  margin-right: 8px;
  background: linear-gradient(45deg, #667eea, #764ba2);
}

.username {
  margin-right: 4px;
  font-weight: 500;
  color: inherit;
}

.guest-menu {
  display: flex;
  gap: 8px; /* 减小登录和注册按钮之间的距离 */
}

.login-btn {
  color: #667eea !important;
  border: 2px solid #667eea;
  background: transparent;
  font-weight: 600;
  border-radius: 12px;
  padding: 10px 18px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
}

.login-btn:hover {
  color: #ffffff !important;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-color: transparent;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.3);
}

.register-btn {
  background: linear-gradient(135deg, #667eea, #764ba2);
  border: 2px solid transparent;
  color: white;
  font-weight: 600;
  border-radius: 12px;
  padding: 10px 20px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.register-btn:hover {
  background: linear-gradient(135deg, #5a67d8, #6b46c1);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

/* Enhanced User Dropdown Menu Styles */
:deep(.el-dropdown-menu) {
  min-width: 260px !important;
  padding: 12px !important;
  border-radius: 20px !important;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
  box-shadow: 0 20px 60px rgba(102, 126, 234, 0.25), 
              0 0 0 1px rgba(102, 126, 234, 0.1) !important;
  border: none !important;
  backdrop-filter: blur(20px);
  position: relative;
  overflow: hidden;
  z-index: 2500 !important;
}

:deep(.el-popper.is-light) {
  z-index: 2500 !important;
}

:deep(.el-dropdown-menu)::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 50%, #667eea 100%);
  background-size: 200% 100%;
  animation: shimmer 3s linear infinite;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

:deep(.el-dropdown-menu__item) {
  padding: 0 !important;
  margin: 6px 0 !important;
  border-radius: 14px !important;
}

:deep(.el-dropdown-menu__item:not(.is-disabled):focus) {
  background-color: transparent !important;
}

.dropdown-item {
  display: flex;
  align-items: center;
  padding: 14px 18px;
  border-radius: 14px;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  color: #4a5568;
  font-weight: 600;
  gap: 14px;
  position: relative;
  overflow: hidden;
  background: transparent;
}

.dropdown-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 4px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  transform: translateX(-4px);
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 0 4px 4px 0;
}

.dropdown-item::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(102, 126, 234, 0) 0%,
    rgba(102, 126, 234, 0.15) 50%,
    rgba(118, 75, 162, 0.15) 100%
  );
  opacity: 0;
  transition: opacity 0.35s ease;
  border-radius: 14px;
}

.dropdown-item:hover::before {
  transform: translateX(0);
}

.dropdown-item:hover::after {
  opacity: 1;
}

.dropdown-item:hover {
  color: #667eea;
  transform: translateX(6px);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.2),
              inset 0 0 0 1px rgba(102, 126, 234, 0.1);
}

.dropdown-item .item-icon {
  font-size: 20px;
  flex-shrink: 0;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.08), rgba(118, 75, 162, 0.08));
  position: relative;
  z-index: 1;
}

.dropdown-item:hover .item-icon {
  transform: scale(1.15) rotate(5deg);
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.15));
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
}

.dropdown-item .item-text {
  flex: 1;
  font-size: 15px;
  letter-spacing: 0.01em;
  position: relative;
  z-index: 1;
}

/* Unread message badge */
.unread-badge {
  margin-left: 8px;
  
  :deep(.el-badge__content) {
    background: linear-gradient(135deg, #ef4444, #dc2626);
    border: none;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 6px;
    height: 18px;
    line-height: 14px;
  }
}

/* Divider styling */
:deep(.el-dropdown-menu__item--divided) {
  margin-top: 12px !important;
  position: relative;
}

:deep(.el-dropdown-menu__item--divided::before) {
  content: '';
  position: absolute;
  top: -6px;
  left: 18px;
  right: 18px;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(102, 126, 234, 0.3) 20%,
    rgba(118, 75, 162, 0.3) 50%,
    rgba(102, 126, 234, 0.3) 80%,
    transparent 100%
  );
}

.logout-item {
  color: #ef4444;
  font-weight: 700;
}

.logout-item .item-icon {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.08), rgba(220, 38, 38, 0.08));
}

.logout-item::before {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

.logout-item::after {
  background: linear-gradient(
    135deg,
    rgba(239, 68, 68, 0) 0%,
    rgba(239, 68, 68, 0.15) 50%,
    rgba(220, 38, 38, 0.15) 100%
  );
}

.logout-item:hover {
  color: #dc2626;
  box-shadow: 0 6px 16px rgba(239, 68, 68, 0.25),
              inset 0 0 0 1px rgba(239, 68, 68, 0.1);
}

.logout-item:hover .item-icon {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.15));
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

/* Invitation Dialog Styles */
.invitation-dialog {
  z-index: 3000 !important;
}

.invitation-dialog :deep(.el-overlay) {
  z-index: 2999 !important;
  backdrop-filter: blur(4px);
}

.invitation-dialog :deep(.el-dialog) {
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: dialogSlideIn 0.3s ease-out;
}

@keyframes dialogSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.invitation-dialog :deep(.el-dialog__header) {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 24px 32px;
  border-radius: 20px 20px 0 0;
  margin: 0;
}

.invitation-dialog :deep(.el-dialog__title) {
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
}

.invitation-dialog :deep(.el-dialog__close) {
  color: white;
  font-size: 24px;
  transition: all 0.2s ease;
}

.invitation-dialog :deep(.el-dialog__close:hover) {
  color: rgba(255, 255, 255, 0.8);
  transform: rotate(90deg);
}

.invitation-dialog :deep(.el-dialog__body) {
  padding: 36px 32px;
  background: white;
}

.invitation-content {
  text-align: center;
}

.code-display {
  margin-bottom: 28px;
}

.code-label {
  font-size: 14px;
  color: #888;
  margin-bottom: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.code-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%);
  border: 2px solid #667eea;
  border-radius: 16px;
  padding: 20px 24px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.code-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
  transition: left 0.5s ease;
}

.code-wrapper:hover {
  border-color: #764ba2;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.25);
  transform: translateY(-2px);
}

.code-wrapper:hover::before {
  left: 100%;
}

.code-value {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 4px;
  color: #667eea;
  font-family: 'Courier New', monospace;
  user-select: all;
}

.copy-icon-btn {
  color: #667eea;
  font-size: 20px;
  padding: 8px;
  transition: all 0.2s ease;
}

.copy-icon-btn:hover {
  color: #764ba2;
  transform: scale(1.15);
  background: rgba(102, 126, 234, 0.1);
  border-radius: 8px;
}

.code-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 24px;
}

.action-btn {
  flex: 1;
  max-width: 220px;
  height: 52px;
  font-size: 15px;
  font-weight: 600;
  border-radius: 12px;
  transition: all 0.3s ease;
  border: none;
  position: relative;
  overflow: hidden;
}

.action-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: translate(-50%, -50%);
  transition: width 0.6s ease, height 0.6s ease;
}

.action-btn:hover::before {
  width: 300px;
  height: 300px;
}

.action-btn span {
  position: relative;
  z-index: 1;
}

.action-btn .el-icon {
  position: relative;
  z-index: 1;
}

.primary-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.primary-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.5);
}

.success-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.4);
}

.success-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.5);
}

.code-info {
  margin-top: 20px;
}

.info-box {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  padding: 16px 20px;
  border: 2px solid rgba(102, 126, 234, 0.15);
  display: flex;
  gap: 12px;
  align-items: flex-start;
  text-align: left;
}

.info-icon {
  font-size: 22px;
  color: #667eea;
  flex-shrink: 0;
  margin-top: 2px;
}

.info-content {
  flex: 1;
}

.info-text {
  color: #555;
  font-size: 14px;
  margin: 0 0 8px 0;
  line-height: 1.6;
  font-weight: 500;
}

.details-link {
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
}

.details-link:hover {
  transform: translateX(4px);
}

.loading-container {
  padding: 40px 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .invitation-dialog :deep(.el-dialog) {
    width: 90% !important;
    margin: 20px auto !important;
  }

  .invitation-dialog :deep(.el-dialog__header) {
    padding: 20px 24px;
  }

  .invitation-dialog :deep(.el-dialog__title) {
    font-size: 1.25rem;
  }

  .invitation-dialog :deep(.el-dialog__body) {
    padding: 24px 20px;
  }

  .code-wrapper {
    padding: 16px 20px;
    flex-direction: column;
    gap: 8px;
  }

  .code-value {
    font-size: 22px;
    letter-spacing: 3px;
  }

  .code-actions {
    flex-direction: column;
    gap: 10px;
  }

  .action-btn {
    max-width: 100%;
    height: 48px;
  }

  .info-box {
    padding: 14px 16px;
  }
}

@media (max-width: 1024px) {
  .nav-container {
    padding: 0 15px;
  }

  .nav-item {
    font-size: 14px;
    padding: 8px 12px;
  }

  .nav-menu-items {
    gap: 6px;
  }
}

@media (max-width: 768px) {
  .nav-container {
    padding: 0 10px;
  }

  .nav-logo {
    transform: scale(0.85);
  }

  .logo-text {
    display: none;
  }

  .nav-item {
    font-size: 13px;
    padding: 6px 10px;
  }

  .nav-menu-items {
    gap: 4px;
  }

  .username {
    display: none;
  }

  .university-row {
    display: none;
  }
}

@media (max-width: 600px) {
  .nav-container {
    position: relative;
  }

  .nav-menu-items {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: linear-gradient(135deg, #ffffff, #f8fafc);
    backdrop-filter: blur(15px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    border-top: 1px solid rgba(102, 126, 234, 0.1);
    display: none;
    flex-direction: column;
    z-index: 1000;
    gap: 0;
    border-radius: 0 0 16px 16px;
  }

  .nav-menu-items.mobile-menu-open {
    display: flex;
  }

  .nav-item {
    width: 100%;
    text-align: center;
    padding: 18px 20px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 0;
    margin: 0 12px;
  }

  .nav-item:last-child {
    border-bottom: none;
    border-radius: 0 0 12px 12px;
  }

  .nav-item:first-child {
    border-radius: 12px 12px 0 0;
  }

  .mobile-menu-toggle {
    display: block;
  }
}
</style>

<style>
/* Global styles for logout confirmation dialog */
.logout-confirmation-dialog {
  border-radius: 16px !important;
  padding: 24px !important;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.15) !important;
}

.logout-confirmation-dialog .el-message-box__header {
  padding-bottom: 20px !important;
}

.logout-confirmation-dialog .el-message-box__title {
  font-size: 20px !important;
  font-weight: 700 !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.logout-confirmation-dialog .el-message-box__content {
  padding: 20px 0 !important;
  font-size: 15px !important;
  color: var(--el-text-color-regular) !important;
}

.logout-confirmation-dialog .el-message-box__btns {
  padding-top: 20px !important;
  display: flex !important;
  gap: 12px !important;
}

.logout-confirm-btn {
  background: linear-gradient(135deg, #f56c6c 0%, #e53e3e 100%) !important;
  border: none !important;
  border-radius: 10px !important;
  padding: 12px 32px !important;
  font-weight: 600 !important;
  box-shadow: 0 4px 12px rgba(245, 108, 108, 0.3) !important;
  transition: all 0.3s ease !important;
}

.logout-confirm-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 16px rgba(245, 108, 108, 0.4) !important;
}

.logout-cancel-btn {
  background: #ffffff !important;
  border: 2px solid #dcdfe6 !important;
  color: var(--el-text-color-regular) !important;
  border-radius: 10px !important;
  padding: 12px 32px !important;
  font-weight: 600 !important;
  transition: all 0.3s ease !important;
}

.logout-cancel-btn:hover {
  border-color: #667eea !important;
  color: #667eea !important;
  background: rgba(102, 126, 234, 0.05) !important;
}
</style>
